import { useEffect } from 'react';
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Database,
  ArrowRight,
  Palette,
  GraduationCap,
} from 'lucide-react';
import { useMappingStore, applyTransform } from '../../../stores/mappingStore';
import type { EnhancedFieldMapping } from '../../../types/mapping';
import { SOURCE_MODULES, MAPPABLE_PROPERTIES } from '../../../types/mapping';

interface DataPreviewPanelProps {
  mappings: EnhancedFieldMapping[];
}

export default function DataPreviewPanel({ mappings }: DataPreviewPanelProps) {
  const { preview, loadPreview, setPreviewRecord } = useMappingStore();

  useEffect(() => {
    if (mappings.length > 0) {
      const firstMapping = mappings[0];
      if (
        !preview ||
        preview.tableName !== firstMapping.tableName ||
        preview.source !== firstMapping.source
      ) {
        loadPreview(firstMapping.source, firstMapping.tableName);
      }
    }
  }, [mappings, preview, loadPreview]);

  if (mappings.length === 0) {
    return (
      <div className="p-3 text-center">
        <Eye className="w-5 h-5 text-white/20 mx-auto mb-2" />
        <p className="text-xs text-white/30">Add mappings to preview live data</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="p-3 text-center">
        <RefreshCw className="w-4 h-4 text-purple-400 animate-spin mx-auto mb-2" />
        <p className="text-xs text-white/40">Loading preview...</p>
      </div>
    );
  }

  if (preview.error) {
    return (
      <div className="p-3">
        <div className="flex items-start gap-2 p-2 rounded-md bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-[10px] text-red-400 font-medium">Preview Error</p>
            <p className="text-[10px] text-red-400/70 mt-0.5">{preview.error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (preview.records.length === 0) {
    return (
      <div className="p-3 text-center">
        <Database className="w-5 h-5 text-white/20 mx-auto mb-2" />
        <p className="text-xs text-white/30">
          No records found in{' '}
          <span className="font-mono text-white/40">{preview.tableName}</span>
        </p>
      </div>
    );
  }

  const currentRecord = preview.records[preview.selectedRecordIndex];
  const SourceIcon = preview.source === 'ad' ? Palette : GraduationCap;
  const sourceConfig = SOURCE_MODULES[preview.source];

  const resolveValue = (
    mapping: EnhancedFieldMapping
  ): { raw: string; transformed: string; usedFallback: boolean } => {
    const rawValue = currentRecord?.[mapping.columnName];
    const raw = rawValue != null ? String(rawValue) : '';
    const usedFallback = rawValue == null || rawValue === '';

    let value = usedFallback ? mapping.fallbackValue : raw;

    if (mapping.transformExpression && value) {
      value = applyTransform(value, mapping.transformExpression);
    }

    return { raw, transformed: value, usedFallback };
  };

  const uniqueTables = [...new Set(mappings.map((m) => m.tableName))];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-3 py-2 bg-[#1a1a2e]/30 rounded-lg border border-white/[0.04]">
        <div className="flex items-center gap-2">
          <SourceIcon className={`w-3.5 h-3.5 ${sourceConfig.color}`} />
          <span className="text-[10px] text-white/40">
            Record {preview.selectedRecordIndex + 1} of {preview.records.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPreviewRecord(preview.selectedRecordIndex - 1)}
            disabled={preview.selectedRecordIndex === 0}
            className="p-1 rounded hover:bg-white/[0.06] text-white/30 hover:text-white/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setPreviewRecord(preview.selectedRecordIndex + 1)}
            disabled={preview.selectedRecordIndex >= preview.records.length - 1}
            className="p-1 rounded hover:bg-white/[0.06] text-white/30 hover:text-white/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => loadPreview(preview.source, preview.tableName)}
            disabled={preview.loading}
            className="p-1 rounded hover:bg-white/[0.06] text-white/30 hover:text-white/50 transition-colors ml-1"
            title="Refresh preview data"
          >
            <RefreshCw className={`w-3 h-3 ${preview.loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {mappings.map((mapping) => {
          const { raw, transformed, usedFallback } = resolveValue(mapping);
          const propertyConfig = MAPPABLE_PROPERTIES.find(
            (p) => p.key === mapping.targetProperty
          );

          return (
            <div
              key={mapping.id}
              className="rounded-lg border border-white/[0.04] bg-[#12121a]/50 overflow-hidden"
            >
              <div className="flex items-center gap-2 px-2.5 py-1.5 bg-white/[0.02] border-b border-white/[0.04]">
                <span className="text-[10px] text-white/40 font-mono truncate">
                  {mapping.columnName}
                </span>
                <ArrowRight className="w-2.5 h-2.5 text-white/20 shrink-0" />
                <span className="text-[10px] text-purple-300 truncate">
                  {propertyConfig?.label || mapping.targetProperty}
                </span>
              </div>

              <div className="px-2.5 py-2 space-y-1">
                <div className="flex items-start gap-2">
                  <span className="text-[10px] text-white/25 w-12 shrink-0 pt-0.5">Raw:</span>
                  <span
                    className={`text-xs font-mono break-all ${
                      raw ? 'text-white/60' : 'text-white/20 italic'
                    }`}
                  >
                    {raw || '(empty)'}
                  </span>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[10px] text-white/25 w-12 shrink-0 pt-0.5">Final:</span>
                  <span
                    className={`text-xs font-mono break-all ${
                      usedFallback
                        ? 'text-amber-400/70'
                        : mapping.transformExpression
                        ? 'text-purple-300'
                        : 'text-emerald-400/80'
                    }`}
                  >
                    {transformed || '(empty)'}
                  </span>
                  {usedFallback && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400/60 border border-amber-500/15 shrink-0">
                      fallback
                    </span>
                  )}
                  {mapping.transformExpression && !usedFallback && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-400/60 border border-purple-500/15 shrink-0">
                      transformed
                    </span>
                  )}
                </div>

                {(mapping.targetProperty === 'fontColor' ||
                  mapping.targetProperty === 'fillColor' ||
                  mapping.targetProperty === 'strokeColor') &&
                  transformed && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-white/25 w-12 shrink-0">Preview:</span>
                      <div
                        className="w-5 h-5 rounded border border-white/[0.1]"
                        style={{ backgroundColor: transformed }}
                      />
                      <span className="text-[10px] text-white/40 font-mono">{transformed}</span>
                    </div>
                  )}

                {(mapping.targetProperty === 'imageUrl' ||
                  mapping.targetProperty === 'imageSrc') &&
                  transformed &&
                  transformed.startsWith('http') && (
                    <div className="mt-1">
                      <span className="text-[10px] text-white/25">Preview:</span>
                      <div className="mt-1 w-full h-16 rounded border border-white/[0.06] overflow-hidden bg-white/[0.02]">
                        <img
                          src={transformed}
                          alt="Preview"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>
          );
        })}
      </div>

      {uniqueTables.length > 1 && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-[10px] text-amber-400/80">
            Mappings span {uniqueTables.length} tables. Preview shows data from{' '}
            <span className="font-mono">{preview.tableName}</span>. Columns from other
            tables will show as empty in this preview.
          </p>
        </div>
      )}
    </div>
  );
}
